import "./bootstrap";
import "../sass/app.scss";
import { createApp } from "vue";
import Clipboard from "vue3-clipboard";
import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "nprogress/nprogress.css";
import App from "./components/App.vue";
import store from "./store";
import getRouter from "./router";
import axios from "axios";
import i18n from "./i18n";

axios.interceptors.response.use(
    (response) => {
        try {
            const method = (response?.config?.method || "").toLowerCase();
            const url = response?.config?.url || "";
            const isMutation = ["post", "put", "patch", "delete"].includes(method);
            const isAdminApi = typeof url === "string" && url.includes("/api/admin/");
            const isNoContent = response?.status === 204;

            const contentType =
                response?.headers?.["content-type"] ||
                response?.headers?.["Content-Type"] ||
                "";
            const isJson =
                typeof response?.data === "object" ||
                String(contentType).toLowerCase().includes("application/json");

            if (isMutation && isAdminApi && !isNoContent && !isJson) {
                const err = new Error("Некорректный ответ сервера");
                err.response = response;
                return Promise.reject(err);
            }

            if (isMutation && isAdminApi && !isNoContent && isJson) {
                store.dispatch(
                    "notification/add",
                    { type: "success", message: "Данные сохранены" },
                    { root: true }
                );
            }
        } catch (e) {}
        return response;
    },
    (error) => Promise.reject(error)
);

const createFilters = (settings) => {
    const currencyFormatter = new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: settings.currency,
        minimumFractionDigits: 0,
    });
    const weightFormatter = new Intl.NumberFormat("ru-RU", {
        style: "decimal",
        minimumFractionDigits: 1,
    });

    return {
        toCurrency(value) {
            if (typeof value !== "number") {
                return value;
            }
            return currencyFormatter.format(value);
        },
        shortArticle(article = "") {
            if (article.length >= 10) {
                return `${article.substr(0, 9)}...`;
            }
            return article;
        },
        knifeString(str = "", count = 40) {
            if (str.length >= count) {
                return `${str.substr(0, count - 1)}...`;
            }
            return str;
        },
        toWeight(value) {
            if (typeof value !== "number") {
                return value;
            }
            return weightFormatter.format(value);
        },
        toDays(n) {
            if (typeof n !== "number") {
                return n;
            }
            const textForms = ["день", "дня", "дней"];
            n = Math.abs(n) % 100;
            const n1 = n % 10;
            if (n > 10 && n < 20) {
                return textForms[2];
            }
            if (n1 > 1 && n1 < 5) {
                return textForms[1];
            }
            if (n1 === 1) {
                return textForms[0];
            }
            return textForms[2];
        },
    };
};

const bootstrap = async () => {
    try {
        const response = await axios.get(`/api/settings`);
        const settings = response.data.data;

        if (settings.system_name) {
            document.title = settings.system_name;
        }

        const locale = settings.language;
        if (locale) {
            i18n.global.locale = locale;
            localStorage.setItem("lang", locale);
        }

        const noShowEnv = localStorage.getItem("noShowSceneEnv");
        if (settings.default_no_env && noShowEnv == null) {
            localStorage.setItem("noShowSceneEnv", 1);
        }

        const lowPerformance = localStorage.getItem("lowPerformance");
        if (
            settings.default_active_performance_regim &&
            lowPerformance == null
        ) {
            localStorage.setItem("lowPerformance", 1);
        }

        const router = getRouter(settings);
        const app = createApp(App);

        app.config.errorHandler = (error) => {
            store.dispatch(
                "notification/add",
                {
                    type: "error",
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        "Ошибка приложения",
                },
                { root: true }
            );
            console.error(error);
        };

        window.addEventListener("unhandledrejection", (event) => {
            const error = event?.reason;
            store.dispatch(
                "notification/add",
                {
                    type: "error",
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        "Unhandled promise rejection",
                },
                { root: true }
            );
            console.error(error);
        });

        const filters = createFilters(settings);
        Object.entries(filters).forEach(([name, handler]) => {
            app.config.globalProperties[`$${name}`] = handler;
        });

        // Автоматическая регистрация компонентов из shared/components
        const componentModules = import.meta.glob(
            "./shared/components/**/*.vue",
            { eager: true }
        );
        Object.keys(componentModules).forEach((key) => {
            const componentName = key.split("/").pop().split(".")[0];
            const module = componentModules[key];
            app.component(componentName, module.default || module);
        });

        app.use(store);
        app.use(router);
        app.use(i18n);
        app.use(Clipboard, { autoSetContainer: true, appendToBody: true });
        app.use(CKEditor);
        app.config.globalProperties.$ckeditorEditor = ClassicEditor;

        await store.dispatch("settings/fetch");
        await store.dispatch("versions/fetch");
        await store.dispatch("versions/checkUpgradeInfo");
        const currentRoute = router.currentRoute.value;
        await store.dispatch("clientOrder/getComplectation", {
            order_cache: currentRoute?.query?.order_cache,
            load_element_id: currentRoute?.query?.load_element_id,
            load_barrier_id: currentRoute?.query?.load_barrier_id,
        });
        store.dispatch("getNoShowSceneEnv");
        store.dispatch("getLowPerformance");

        await router.isReady();
        app.mount("#app");
    } catch (err) {
        console.error(err);
    }
};

bootstrap();
